import type { StateCreator } from 'zustand'
import { generateId } from '../../lib/utils.js'
import type { GridUndoStep } from '../../lib/gridUndo.js'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GridUndoEntry {
  id:    string
  /** Libellé de l'action annulable : « ajout à la grille », « rediffusion »… */
  label: string
  /** Journée d'antenne où l'action a eu lieu — l'annulation peut viser un autre jour. */
  date:  string
  /** Horodatage poste, pour l'infobulle du bouton. */
  at:    number
  step:  GridUndoStep
}

// ─── Slice ────────────────────────────────────────────────────────────────────

/**
 * Pile d'annulation de la grille. Elle vit en mémoire de l'onglet : une photo
 * vieille d'une session n'aurait plus rien à voir avec la grille d'antenne, et
 * la rejouer à l'aveugle serait pire que de ne rien proposer.
 */
export interface UndoSlice {
  gridUndoStack: GridUndoEntry[]
  /** Ce que les annulations ont défait, et qu'un « Rétablir » peut remettre. */
  gridRedoStack: GridUndoEntry[]
  gridUndoBusy:  boolean

  /** Action d'opérateur : elle ouvre une branche neuve, le rétablissement tombe. */
  pushGridUndoEntry:    (entry: Omit<GridUndoEntry, 'id' | 'at'>) => void
  /** Une annulation vient d'aboutir : ce qu'elle a défait devient rétablissable. */
  pushGridRedoEntry:    (entry: Omit<GridUndoEntry, 'id' | 'at'>) => void
  /** Un rétablissement vient d'aboutir : l'action redevient annulable. */
  restoreGridUndoEntry: (entry: Omit<GridUndoEntry, 'id' | 'at'>) => void
  dropGridUndoEntry:    (id: string) => void
  dropGridRedoEntry:    (id: string) => void
  clearGridUndo:        () => void
  setGridUndoBusy:      (busy: boolean) => void
}

/** Au-delà, les photos les plus anciennes ne décrivent plus la grille courante. */
const MAX_DEPTH = 20

const stamp = (entry: Omit<GridUndoEntry, 'id' | 'at'>): GridUndoEntry =>
  ({ ...entry, id: generateId(), at: Date.now() })

export const createUndoSlice: StateCreator<UndoSlice, [], [], UndoSlice> = (set) => ({
  gridUndoStack: [],
  gridRedoStack: [],
  gridUndoBusy:  false,

  // Une action neuve rend caduc tout ce qui restait à rétablir : la grille a
  // repris un autre chemin, et rejouer l'ancien par-dessus poserait des
  // éléments dans un décor qui n'est plus celui de leur photo.
  pushGridUndoEntry: (entry) => set(state => ({
    gridUndoStack: [...state.gridUndoStack, stamp(entry)].slice(-MAX_DEPTH),
    gridRedoStack: [],
  })),

  pushGridRedoEntry: (entry) => set(state => ({
    gridRedoStack: [...state.gridRedoStack, stamp(entry)].slice(-MAX_DEPTH),
  })),

  restoreGridUndoEntry: (entry) => set(state => ({
    gridUndoStack: [...state.gridUndoStack, stamp(entry)].slice(-MAX_DEPTH),
  })),

  dropGridUndoEntry: (id) => set(state => ({
    gridUndoStack: state.gridUndoStack.filter(entry => entry.id !== id),
  })),

  dropGridRedoEntry: (id) => set(state => ({
    gridRedoStack: state.gridRedoStack.filter(entry => entry.id !== id),
  })),

  clearGridUndo: () => set({ gridUndoStack: [], gridRedoStack: [] }),

  setGridUndoBusy: (busy) => set({ gridUndoBusy: busy }),
})
